"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.audioService = void 0;
class AudioService {
    constructor() {
        this.ctx = null;
        this.enabled = true;
        this.ringInterval = null;
    }
    init() {
        if (!this.ctx) {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (AudioContextClass) {
                this.ctx = new AudioContextClass();
            }
        }
        if (this.ctx && this.ctx.state === 'suspended') {
            this.ctx.resume().catch(() => { });
        }
    }
    isEnabled() {
        return this.enabled && localStorage.getItem('meci_notif_sounds') !== 'false';
    }
    playMessageSent() {
        if (!this.isEnabled())
            return;
        try {
            this.init();
            if (!this.ctx)
                return;
            const ctx = this.ctx;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.setValueAtTime(600, ctx.currentTime);
            osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.1);
            gain.gain.setValueAtTime(0.15, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + 0.1);
        }
        catch (e) {
            console.warn('Audio play failed:', e);
        }
    }
    playMessageReceived() {
        if (!this.isEnabled())
            return;
        try {
            this.init();
            if (!this.ctx)
                return;
            const ctx = this.ctx;
            const playTone = (freq, startTime, duration) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.type = 'triangle';
                osc.frequency.setValueAtTime(freq, startTime);
                gain.gain.setValueAtTime(0.18, startTime);
                gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
                osc.start(startTime);
                osc.stop(startTime + duration);
            };
            playTone(587.33, ctx.currentTime, 0.12); // D5
            playTone(880.00, ctx.currentTime + 0.08, 0.2); // A5
        }
        catch (e) {
            console.warn('Audio play failed:', e);
        }
    }
    // ── Voice Session: Join & Leave ──────────────────────────────────
    playJoinVoice() {
        if (!this.isEnabled())
            return;
        try {
            this.init();
            if (!this.ctx)
                return;
            const ctx = this.ctx;
            if (ctx.state === 'suspended') {
                ctx.resume().catch(() => { });
            }
            // Discord-style ascending chime (bright, crisp & clearly audible even when deafened)
            const now = ctx.currentTime;
            const playTone = (freq, startTime, duration, vol = 0.25) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.type = 'triangle';
                osc.frequency.setValueAtTime(freq, startTime);
                gain.gain.setValueAtTime(vol, startTime);
                gain.gain.exponentialRampToValueAtTime(0.005, startTime + duration);
                osc.start(startTime);
                osc.stop(startTime + duration);
            };
            // Crisp 3-tone chime: C5 -> E5 -> G5
            playTone(523.25, now, 0.16, 0.26); // C5
            playTone(659.25, now + 0.08, 0.16, 0.28); // E5
            playTone(783.99, now + 0.16, 0.32, 0.30); // G5
        }
        catch (e) {
            console.warn('Audio play failed:', e);
        }
    }
    playLeaveVoice() {
        if (!this.isEnabled())
            return;
        try {
            this.init();
            if (!this.ctx)
                return;
            const ctx = this.ctx;
            // Discord-style descending chime
            const now = ctx.currentTime;
            const playTone = (freq, startTime, duration) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.type = 'sine';
                osc.frequency.setValueAtTime(freq, startTime);
                gain.gain.setValueAtTime(0.18, startTime);
                gain.gain.exponentialRampToValueAtTime(0.005, startTime + duration);
                osc.start(startTime);
                osc.stop(startTime + duration);
            };
            playTone(783.99, now, 0.14); // G5
            playTone(659.25, now + 0.09, 0.14); // E5
            playTone(493.88, now + 0.18, 0.28); // B4
        }
        catch (e) {
            console.warn('Audio play failed:', e);
        }
    }
    // ── Mute & Unmute Tones (Discord Style) ───────────────────────────
    playMute() {
        if (!this.isEnabled())
            return;
        try {
            this.init();
            if (!this.ctx)
                return;
            const ctx = this.ctx;
            const now = ctx.currentTime;
            const playTone = (freq, startTime, duration) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.type = 'sine';
                osc.frequency.setValueAtTime(freq, startTime);
                gain.gain.setValueAtTime(0.16, startTime);
                gain.gain.exponentialRampToValueAtTime(0.005, startTime + duration);
                osc.start(startTime);
                osc.stop(startTime + duration);
            };
            // Two-tone descending muted boop
            playTone(480, now, 0.09);
            playTone(360, now + 0.08, 0.12);
        }
        catch (e) {
            console.warn('Mute audio play failed:', e);
        }
    }
    playUnmute() {
        if (!this.isEnabled())
            return;
        try {
            this.init();
            if (!this.ctx)
                return;
            const ctx = this.ctx;
            const now = ctx.currentTime;
            const playTone = (freq, startTime, duration) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.type = 'sine';
                osc.frequency.setValueAtTime(freq, startTime);
                gain.gain.setValueAtTime(0.16, startTime);
                gain.gain.exponentialRampToValueAtTime(0.005, startTime + duration);
                osc.start(startTime);
                osc.stop(startTime + duration);
            };
            // Two-tone ascending unmuted chirp
            playTone(360, now, 0.09);
            playTone(540, now + 0.08, 0.14);
        }
        catch (e) {
            console.warn('Unmute audio play failed:', e);
        }
    }
    // ── Deafen & Undeafen Tones ──────────────────────────────────────
    playDeafen() {
        if (!this.isEnabled())
            return;
        try {
            this.init();
            if (!this.ctx)
                return;
            const ctx = this.ctx;
            const now = ctx.currentTime;
            const playTone = (freq, startTime, duration) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.type = 'triangle';
                osc.frequency.setValueAtTime(freq, startTime);
                gain.gain.setValueAtTime(0.17, startTime);
                gain.gain.exponentialRampToValueAtTime(0.005, startTime + duration);
                osc.start(startTime);
                osc.stop(startTime + duration);
            };
            // Metallic descending deafen tone
            playTone(500, now, 0.1);
            playTone(380, now + 0.08, 0.1);
            playTone(260, now + 0.16, 0.18);
        }
        catch (e) {
            console.warn('Deafen audio play failed:', e);
        }
    }
    playUndeafen() {
        if (!this.isEnabled())
            return;
        try {
            this.init();
            if (!this.ctx)
                return;
            const ctx = this.ctx;
            const now = ctx.currentTime;
            const playTone = (freq, startTime, duration) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.type = 'triangle';
                osc.frequency.setValueAtTime(freq, startTime);
                gain.gain.setValueAtTime(0.17, startTime);
                gain.gain.exponentialRampToValueAtTime(0.005, startTime + duration);
                osc.start(startTime);
                osc.stop(startTime + duration);
            };
            // Bright ascending undeafen tone
            playTone(260, now, 0.1);
            playTone(380, now + 0.08, 0.1);
            playTone(560, now + 0.16, 0.22);
        }
        catch (e) {
            console.warn('Undeafen audio play failed:', e);
        }
    }
    playToggleClick() {
        if (!this.isEnabled())
            return;
        try {
            this.init();
            if (!this.ctx)
                return;
            const ctx = this.ctx;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.setValueAtTime(400, ctx.currentTime);
            gain.gain.setValueAtTime(0.08, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + 0.05);
        }
        catch (e) {
            console.warn('Audio play failed:', e);
        }
    }
    playCallRingtone() {
        if (!this.isEnabled())
            return;
        this.stopCallRingtone();
        const ring = () => {
            try {
                this.init();
                if (!this.ctx)
                    return;
                const ctx = this.ctx;
                const now = ctx.currentTime;
                const playTone = (freq, start, dur) => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.connect(gain);
                    gain.connect(ctx.destination);
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(freq, start);
                    gain.gain.setValueAtTime(0.12, start);
                    gain.gain.exponentialRampToValueAtTime(0.01, start + dur);
                    osc.start(start);
                    osc.stop(start + dur);
                };
                playTone(440, now, 0.4);
                playTone(480, now, 0.4);
                playTone(440, now + 0.5, 0.4);
                playTone(480, now + 0.5, 0.4);
            }
            catch (e) {
                console.warn('Ringtone play error:', e);
            }
        };
        ring();
        this.ringInterval = setInterval(ring, 3000);
    }
    stopCallRingtone() {
        if (this.ringInterval) {
            clearInterval(this.ringInterval);
            this.ringInterval = null;
        }
    }
}
exports.audioService = new AudioService();
